"use client"

import { useQuery } from "@tanstack/react-query"
import { useRegion } from "./use-region"
import { authFetchJson } from "@/shared/lib/auth-fetch"

const REGION_BY_SLUG_STALE_MS = 5 * 60 * 1000 // 5 min – avoid refetch when switching tabs/sections

async function fetchRegionBySlug(regionSlug: string) {
  const res = await authFetchJson<{ id: number; slug: string; name: string }>(`/regions/by-slug/${regionSlug}/id`)
  if ((res as any)?.success === false) {
    if ((res as any).status === 404) return null
    throw new Error(res.message || "Failed to fetch region")
  }
  const regionData = (res as any)?.success ? (res.data?.data ?? res.data) : res
  return regionData && regionData.id ? regionData : null
}

/**
 * Hook to get the current region ID from the URL.
 * Uses React Query so region is cached when switching tabs/sections (faster filter-search load).
 */
export function useRegionId() {
  const { regionSlug } = useRegion()

  const query = useQuery({
    queryKey: ["region", "by-slug", regionSlug ?? ""],
    queryFn: () => fetchRegionBySlug(regionSlug!),
    enabled: !!regionSlug,
    staleTime: REGION_BY_SLUG_STALE_MS,
    refetchOnWindowFocus: false,
  })

  const region = query.data ?? null
  const regionId = region?.id ?? null

  return {
    regionId,
    regionSlug: regionSlug ?? null,
    region,
    isLoading: query.isLoading,
  }
}

